import type { RouteLocationNormalizedLoadedGeneric } from "vue-router";
import { useAuthStore } from "../stores/auth";
import type { AuthFlow, PROVIDER_NAMES, UserModel } from "../types";
import { getHygienaOptions } from "../utils/providers.ts";
import { useForgotPassword, useIssueAuthTicket, useRedeemAuthTicket } from "./user";

type LoginWithProviderOptions = {
  loginHint?: string;
  password?: string;
  providerName?: PROVIDER_NAMES;
};

export function useAuthApi(to?: RouteLocationNormalizedLoadedGeneric) {
  const $nuxt = useNuxtApp();
  const {
    $i18n: { t },
  } = useNuxtApp();
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  const { ticketQueryParam, goToUnauthorizedRedirectPage, goHome, goToExternalApp } = useAuthQueryParams(to);
  const notification = useGlobalNotification();
  const { isMultiTenant } = useIsMultiTenant();
  const forgotPasswordMutate = useForgotPassword();
  const issueAuthTicketMutate = useIssueAuthTicket();
  const redeemAuthTicketMutate = useRedeemAuthTicket();

  const currentUser = computed(() => authStore.currentUser);

  async function loginWithProvider(options: LoginWithProviderOptions = {}): Promise<UserModel | undefined> {
    await authStore.initialize(options.providerName);

    const loginOptions = options.loginHint
      ? {
          loginHint: options.loginHint,
          password: options.password,
        }
      : undefined;

    const account = await authStore.login(loginOptions);

    return account ? await authStore.refreshCurrentUser() : undefined;
  }

  async function attemptLogin(options: LoginWithProviderOptions = {}) {
    const result = await loginWithProvider(options);
    if (result) {
      return goHome();
    }
    return goToUnauthorizedRedirectPage();
  }

  const attemptRedeemTicket = async () => {
    if (!ticketQueryParam.value?.length || !isMultiTenant.value) return;

    const hygienaConfig = getHygienaOptions(config);
    const ticketId = ticketQueryParam.value;

    devConsole.info("🔑 ckAuth: Attempting to redeem auth ticket", ticketId);

    const result = await redeemAuthTicketMutate.safeMutate({
      appName: hygienaConfig.appName,
      ticketId,
    });

    if (!result) {
      devConsole.error("❌ ckAuth: Failed to redeem auth ticket", ticketId);
      return;
    }

    authStore.initialize();
    const token = await authStore.refreshToken(result.accessToken);
    if (token) {
      devConsole.info("✅ ckAuth: Successfully redeemed auth ticket", ticketId);
    }
  };

  async function refreshCurrentUser() {
    const result = authStore.refreshCurrentUser();
    if (result) {
      devConsole.info("📣 ckAuth: custom Nuxt Hook call ckauth:user-loaded");
      $nuxt.callHook("ckauth:user-loaded", {
        currentUser: result,
      });
    }
    return result;
  }

  async function requestForgotPasswordEmail(data: { email: string }) {
    const hygienaConfig = getHygienaOptions(config);
    return await forgotPasswordMutate.safeMutate({
      appName: hygienaConfig.appName,
      ...data,
    });
  }

  async function handleLogout() {
    await authStore.logout();
    if (!authStore.isAuthed) {
      authStore.resetCurrentUser();
      devConsole.info("🔒 Auth logged out successfully.");
      // force rerun middlewares
      await reloadNuxtApp();
    }
  }

  async function withResetAuthFlow<T>(callback: (authFlow: AuthFlow) => Promise<T>): Promise<T> {
    try {
      return await callback(authStore.currentAuthFlow);
    } finally {
      await authStore.resetAuthFlow();
    }
  }

  async function initAuthApi() {
    await attemptRedeemTicket();
    await authStore.refresh();
  }

  const navigateToExternalApp = async (targetApp: { appUrl: string | URL; appName: string }) => {
    let ticketId = "";
    if (authStore.isAuthed) {
      const hygienaOptions = getHygienaOptions(config);
      const ticket = await issueAuthTicketMutate.safeMutate({
        appName: hygienaOptions.appName,
        targetApp: targetApp.appName,
      });

      ticketId = ticket.ticketId;
    }

    return goToExternalApp(targetApp.appUrl, ticketId);
  };

  return {
    authStore,
    currentUser,
    loginWithProvider,
    attemptLogin,
    initAuthApi,
    navigateToExternalApp,
    requestForgotPasswordEmail,
    refreshCurrentUser,
    handleLogout,
    withResetAuthFlow,
  };
}
